@file:Suppress("DEPRECATION")
package org.example.newportfolio.components.sections.start

import androidx.compose.runtime.Composable
import org.example.newportfolio.components.sections.start.components.SkillsSectionDivider
import org.example.newportfolio.components.sections.start.components.StartSectionGridStyle
import org.example.newportfolio.components.sections.start.components.StartSectionImageStyle
import org.example.newportfolio.components.widgets.button.PrimaryButton
import org.example.newportfolio.components.widgets.section.SectionContainer
import org.example.newportfolio.components.widgets.section.SectionContainerStyle
import org.example.newportfolio.models.Section
import org.example.newportfolio.theme.brand
import org.example.newportfolio.theme.icons.ArrowForwardIcon
import org.example.newportfolio.theme.icons.IconOnBrandColorVariant
import org.example.newportfolio.theme.icons.IconStyle
import org.example.newportfolio.theme.icons.ScrollMouseIcon
import org.example.newportfolio.utils.Res
import com.varabyte.kobweb.compose.css.CSSPosition
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.css.functions.RadialGradient
import com.varabyte.kobweb.compose.css.functions.radialGradient
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.rememberPageContext
import kotlinx.browser.window
import com.varabyte.kobweb.silk.components.layout.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.components.style.addVariant
import com.varabyte.kobweb.silk.components.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.components.style.toAttrs
import com.varabyte.kobweb.silk.components.style.toModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.example.newportfolio.theme.fonts.*
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

val StartSectionVariant by SectionContainerStyle.addVariant {
    val colorPalette = colorMode.toPalette()
    val isLight = colorMode.isLight

    // The section's background gradient is ambient light only. The
    // *primary* light source on the subject is the `.hero-spotlight`
    // halo rendered inside ImageContent — that's anchored to the photo
    // and therefore follows it across every breakpoint. This section
    // gradient just sets the overall mood of the hero canvas.
    //
    // Light vs dark intensity:
    //   Dark mode wants a rich, present glow against near-black.
    //   Light mode wants the OPPOSITE — a barely-there pale lavender
    //   wash on the pearlescent page. The same brand purple at a much
    //   lower alpha + a larger transparent stop reads as a soft,
    //   diffused lavender bloom rather than a saturated blob.
    val washNear = if (isLight) 0.16f else 0.45f   // mobile / top of canvas
    val washMid  = if (isLight) 0.14f else 0.40f
    val washFar  = if (isLight) 0.11f else 0.35f   // desktop (photo on right)
    val stopNear = if (isLight) 58 else 38         // transparent stop % — bigger = more diffused
    val stopMid  = if (isLight) 60 else 42
    val stopFar  = if (isLight) 72 else 55

    // Per breakpoint:
    //   ZERO/SM/MD — photo stacks on top of text (ColumnReverse). The
    //     ambient wash centers near the top where the photo lives.
    //   LG/XL — photo sits on the right (Row). The wash shifts to ~70%
    //     horizontal so the right half of the hero feels lit while the
    //     left (text) sits in a slightly cooler zone — the contrast the
    //     Smith / Joy Seno reference shots use to pull the eye to the
    //     subject.
    Breakpoint.ZERO {
        Modifier
            // Auto height on phones. A fixed 75vh/38rem was SMALLER than the
            // stacked content (photo + title + subtitle + CTA), so with the
            // Column's Center arrangement the surplus bled UP into the sticky
            // header (#2) and DOWN into the skills strip (#3). Auto height =
            // exact content height, so Center distributes zero extra space and
            // nothing overflows. Padding gives header clearance + bottom rhythm.
            //
            // MUST be the padding-top/bottom longhands: Modifier.padding(top=,
            // bottom=) emits the `padding` shorthand, which resets left/right
            // to 0 and wipes out SectionContainerStyle's responsive side
            // padding at every breakpoint ≥ this one (text was hitting the
            // viewport edge on desktop).
            .styleModifier {
                property("padding-top", "28px")
                property("padding-bottom", "40px")
            }
            .backgroundImage(
                radialGradient(RadialGradient.Shape.Circle, CSSPosition(x = 50.percent, y = 25.percent)) {
                    add(colorPalette.brand.primary.toRgb().copyf(alpha = washNear))
                    add(Colors.Transparent, stopNear.percent)
                }
            )
    }
    Breakpoint.SM {
        Modifier
            .styleModifier {
                property("padding-top", "32px")
                property("padding-bottom", "44px")
            }
            .backgroundImage(
                radialGradient(RadialGradient.Shape.Circle, CSSPosition(x = 50.percent, y = 30.percent)) {
                    add(colorPalette.brand.primary.toRgb().copyf(alpha = washNear))
                    add(Colors.Transparent, stopNear.percent)
                }
            )
    }
    Breakpoint.MD {
        Modifier
            // MD still stacks (ColumnReverse) → same overflow class as ZERO/SM.
            // LG/XL switch to a Row and keep their vh heights below.
            .styleModifier {
                property("padding-top", "40px")
                property("padding-bottom", "48px")
            }
            .backgroundImage(
                radialGradient(RadialGradient.Shape.Circle, CSSPosition(x = 50.percent, y = 30.percent)) {
                    add(colorPalette.brand.primary.toRgb().copyf(alpha = washMid))
                    add(Colors.Transparent, stopMid.percent)
                }
            )
    }
    Breakpoint.LG {
        Modifier
            .height(95.vh)
            .backgroundImage(
                radialGradient(RadialGradient.Shape.Ellipse, CSSPosition(x = 70.percent, y = 50.percent)) {
                    add(colorPalette.brand.primary.toRgb().copyf(alpha = washFar))
                    add(Colors.Transparent, stopFar.percent)
                }
            )
    }
    Breakpoint.XL {
        Modifier
            .height(100.vh)
            .backgroundImage(
                radialGradient(RadialGradient.Shape.Ellipse, CSSPosition(x = 72.percent, y = 50.percent)) {
                    add(colorPalette.brand.primary.toRgb().copyf(alpha = washFar))
                    add(Colors.Transparent, stopFar.percent)
                }
            )
    }
}

@Composable
fun StartSection() {
    val pageContext = rememberPageContext()
    val breakpoint = rememberBreakpoint()

    Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        SectionContainer(
            // position: relative so the absolutely-positioned `.hero-orbs`
            // div below uses this container as its positioning origin
            // (otherwise orbs would escape to the body).
            modifier = Modifier.styleModifier { property("position", "relative") },
            variant = StartSectionVariant,
            section = Section.Start,
            verticalArrangement = Arrangement.Center
        ) {
            // Three large blurred radial gradients that drift on their
            // own CSS keyframe loops. Pure compositor work — no JS, no
            // layout. See animations.css for the keyframe definitions.
            HeroOrbs()
            Div(attrs = StartSectionGridStyle.toAttrs()) {
                TextContent(
                    breakpoint = breakpoint,
                    onExploreWorksClicked = { pageContext.router.navigateTo(Section.Works.href) }
                )
                ImageContent(breakpoint = breakpoint)
            }
            ScrollHint(modifier = Modifier.displayIfAtLeast(Breakpoint.MD).align(Alignment.Start))
        }
        SkillsSectionDivider()
    }
}

@Composable
private fun HeroOrbs() {
    Div(attrs = { classes("hero-orbs") }) {
        Div(attrs = { classes("floating-orb", "floating-orb-1") })
        Div(attrs = { classes("floating-orb", "floating-orb-2") })
        Div(attrs = { classes("floating-orb", "floating-orb-3") })
    }
}

@Composable
private fun ScrollHint(
    modifier: Modifier
) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Start
    ) {
        ScrollMouseIcon(modifier = IconStyle.toModifier().size(36.px))
        SpanText(
            modifier = TextStyle.toModifier(LabelMediumTextStyle, TextStylePrimaryColor)
                .textAlign(TextAlign.Start),
            text = Res.Strings.SCROLL_HINT
        )
    }
}

@Composable
private fun TextContent(
    breakpoint: Breakpoint,
    onExploreWorksClicked: () -> Unit,
) {
    Column(
        modifier = Modifier.flex(1),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = if (breakpoint >= Breakpoint.LG) Alignment.Start else Alignment.CenterHorizontally
    ) {
        SpanText(
            modifier = TextStyle.toModifier(DisplayTextStyle, TextStylePrimaryColor)
                .textAlign(if (breakpoint > Breakpoint.MD) TextAlign.Start else TextAlign.Center),
            text = Res.Strings.START_SECTION_TITLE
        )
        SpanText(
            modifier = TextStyle.toModifier(SubDisplayTextStyle, TextStyleSecondaryColor)
                .maxWidth(if (breakpoint >= Breakpoint.SM) 75.percent else 90.percent)
                .margin(top = 3.px)
                .textAlign(if (breakpoint > Breakpoint.MD) TextAlign.Start else TextAlign.Center),
            text = Res.Strings.START_SECTION_SUBTITLE
        )

        Row(
            // 50px subtitle→CTA gap is desktop-scale; on a phone it both wastes
            // a third of the hero budget and was the biggest single push driving
            // the CTA into the skills strip. 28px keeps a clear, tappable gap.
            modifier = Modifier
                .margin(top = if (breakpoint >= Breakpoint.LG) 50.px else 28.px)
                .gap(if (breakpoint >= Breakpoint.LG) 20.px else 16.px),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = if (breakpoint >= Breakpoint.LG) Arrangement.Start else Arrangement.Center
        ) {
            PrimaryButton(
                text = Res.Strings.START_SECTION_BUTTON_TEXT,
                icon = {
                    ArrowForwardIcon(modifier = IconStyle.toModifier(IconOnBrandColorVariant))
                },
                onClick = {
                    onExploreWorksClicked()
                }
            )
        }
    }
}

@Composable
private fun ImageContent(
    breakpoint: Breakpoint
) {
    val colorMode = com.varabyte.kobweb.silk.theme.colors.ColorMode.current
    val heroImage = if (colorMode.isDark) Res.Images.ME_DARK else Res.Images.ME_LIGHT

    // Spotlight gradient is mode-aware (set inline so it can read the live
    // colour mode — a CSS class can't). Dark = indigo glow against
    // near-black. Light = pale lavender bloom against the pearlescent
    // page; the dark indigo would read as a grey smudge on white, so the
    // light variant is much paler and higher-key.
    val spotlightGradient = if (colorMode.isDark) {
        "radial-gradient(ellipse 65% 60% at 50% 42%, " +
            "rgba(95,80,195,0.50) 0%, rgba(70,90,175,0.30) 30%, " +
            "rgba(45,40,110,0.15) 55%, transparent 78%)"
    } else {
        "radial-gradient(ellipse 70% 62% at 50% 44%, " +
            "rgba(192,168,242,0.42) 0%, rgba(208,190,244,0.22) 35%, " +
            "rgba(230,220,250,0.10) 60%, transparent 80%)"
    }

    // Ground the figure in the lower third of the hero on desktop, matching
    // the Smith / Joy Seno reference composition (subject low, head near
    // mid-height). Only on the LG/XL Row layout — on mobile (ColumnReverse,
    // photo stacked on text) a downward shift would just open a gap.
    // translateY is layout-safe: it never creates overflow/scroll, and the
    // figure's baked bottom-mist hides any bleed past the hero edge.
    val lowerShift = when {
        breakpoint >= Breakpoint.XL -> "120px"
        breakpoint >= Breakpoint.LG -> "88px"
        else -> "0px"
    }

    Box(
        modifier = Modifier.flex(1),
        contentAlignment = Alignment.Center
    ) {
        // Inner Box: aspect-matched to the cutout (~0.60 w/h) and given
        // position: relative so the spotlight halo + the photo can stack
        // as positioned siblings. Centering the inner Box inside the
        // outer flex(1) Box keeps the subject in the middle of its
        // column at every breakpoint; translateY then drops it lower.
        Box(
            modifier = StartSectionImageStyle.toModifier()
                .styleModifier {
                    property("position", "relative")
                    property("transform", "translateY($lowerShift)")
                },
            contentAlignment = Alignment.Center
        ) {
            // Spotlight halo — large soft radial that bleeds beyond the
            // photo's bounding box on every side. Position + blur +
            // breathe animation live in `.hero-spotlight` (animations.css);
            // the colour is set inline so it can follow the colour mode.
            Div(attrs = {
                classes("hero-spotlight")
                style { property("background", spotlightGradient) }
            })

            // Subject photo on top of the halo.
            Img(
                src = heroImage,
                attrs = Modifier
                    .fillMaxSize()
                    .objectFit(ObjectFit.Contain)
                    .styleModifier {
                        // Pin the figure to the bottom of the frame so
                        // it stands on the floor and head-room reads
                        // as composition rather than awkward space.
                        property("object-position", "50% 100%")
                        // Above the halo.
                        property("position", "relative")
                        property("z-index", "1")

                        // NOTE: the bottom mist fade is now baked into the
                        // image's alpha channel + colour (see the offline
                        // compositing pass in scripts/process_hero.py — a
                        // smoothstep alpha taper plus a push toward the
                        // page's deep-purple bg, so the dissolve reads as
                        // mist rather than a clean cut). The figure is also
                        // colour-graded and light-wrapped toward the UI's
                        // ambient indigo there. No CSS mask here anymore —
                        // a second fade would just double-darken the band.
                    }
                    .toAttrs {
                        attr("alt", "Mohamed Elgohary - Software Engineer & Product Builder")
                        attr("fetchpriority", "high")
                        attr("decoding", "async")
                        attr("loading", "eager")
                    }
            )
        }
    }
}